/**
 * Router minimo del checkout-widget (plan 7.3.1).
 *
 * El widget tiene una sola ruta real, `/session`. No se usa un router de terceros:
 * ni el bundle ni la superficie de ataque lo justifican para una unica pantalla.
 *
 * Cualquier otra ruta, o una sesion con parametros incompletos, muestra un error
 * NEUTRO: sin datos del intent, sin el client_secret y sin pistas sobre si el
 * recurso existe. Un iframe montado a mano no debe poder usar esta pantalla como
 * oraculo.
 */
import type { JSX } from 'react';

import { ErrorMessage } from './components/ErrorMessage';
import { t } from './i18n';
import type { SessionFragment, SessionQuery } from './session/parseFragment';
import { SessionPage } from './session/SessionPage';

export interface AppProps {
  /** `window.location.pathname` del documento, resuelto en el bootstrap. */
  path: string;
  fragment: SessionFragment;
  query: SessionQuery;
  apiBaseUrl: string;
  /**
   * `true` cuando el checkout es el documento superior (modo pagina) en vez de ir embebido en la
   * del comercio. Se resuelve en el bootstrap y no aqui para que el componente siga siendo puro.
   */
  isTopLevel: boolean;
}

const SESSION_PATH = '/session';

export function App(props: AppProps): JSX.Element {
  const { path, fragment, query, apiBaseUrl, isTopLevel } = props;
  const locale = query.locale;

  if (path !== SESSION_PATH) {
    return (
      <ErrorMessage
        code="route_not_found"
        message={t(locale, 'route.not_found.body')}
        locale={locale}
      />
    );
  }

  const { publicKey } = query;
  const { clientSecret, nonce } = fragment;

  // El nonce autentica el canal postMessage con el SDK, asi que solo hace falta EMBEBIDO. En modo
  // pagina —el checkout es el documento, no hay SDK ni ventana padre— no hay canal que autenticar y
  // exigirlo dejaba la pagina inservible: nadie lo acuna. La sesion la protegen igualmente la clave
  // publicable y el client_secret.
  const nonceRequired = !isTopLevel;

  if (publicKey === null || clientSecret === null || (nonceRequired && nonce === null)) {
    // Sin estos parametros no hay canal ni sesion: el SDK resolvera por su
    // timeout de 10 s con code `session_invalid`.
    return (
      <ErrorMessage
        code="session_invalid"
        message={t(locale, 'error.session_invalid')}
        locale={locale}
      />
    );
  }

  return (
    <SessionPage
      clientSecret={clientSecret}
      publicKey={publicKey}
      nonce={nonce ?? ''}
      locale={locale}
      apiBaseUrl={apiBaseUrl}
    />
  );
}
